import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import {
  ArrowLeft, Plus, Copy, ExternalLink, Eye, MousePointerClick,
  Mail, Trash2, RefreshCw, Building2,
} from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["emailed", "opened", "clicked", "booked", "signed", "won", "lost"];
const STATUS_COLOR = {
  emailed: "bg-zinc-100 text-zinc-700 border-zinc-300",
  opened: "bg-blue-50 text-blue-700 border-blue-300",
  clicked: "bg-amber-50 text-amber-700 border-amber-300",
  booked: "bg-purple-50 text-purple-700 border-purple-300",
  signed: "bg-emerald-50 text-emerald-700 border-emerald-300",
  won: "bg-green-100 text-green-800 border-green-400 font-semibold",
  lost: "bg-red-50 text-red-600 border-red-200",
};

const PUBLIC_ORIGIN = window.location.origin;

function fmtRelative(iso) {
  if (!iso) return "—";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.round(mins / 60);
    if (hrs < 48) return `${hrs}h`;
    return `${Math.round(hrs / 24)}d`;
  } catch { return "—"; }
}

function formatApiError(err, fallback) {
  // FastAPI/pydantic validation errors come back as an ARRAY of objects under
  // `detail`. Other endpoints return a string. Normalize both shapes so toast
  // doesn't choke trying to render an object.
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d) => {
      const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== "body").join(".") : "";
      return field ? `${field}: ${d.msg}` : d.msg;
    }).filter(Boolean).join("; ") || fallback;
  }
  return fallback;
}

export default function InstructorCorporate() {
  const [data, setData] = useState({ prospects: [], summary: { total: 0, by_status: {} } });
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({ company: "", contact_name: "", contact_email: "", seats_estimate: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/admin/corporate/prospects");
      setData(d);
    } catch {
      toast.error("Couldn't load corporate pipeline");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    if (!form.company.trim()) { toast.error("Company name required"); return; }
    setSaving(true);
    try {
      const payload = {
        company: form.company.trim(),
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null,
        seats_estimate: form.seats_estimate ? parseInt(form.seats_estimate, 10) : null,
        notes: form.notes.trim() || null,
      };
      await api.post("/admin/corporate/prospects", payload);
      toast.success(`${payload.company} added — tracking link copied`);
      setForm({ company: "", contact_name: "", contact_email: "", seats_estimate: "", notes: "" });
      setShowAddForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't add prospect"));
    } finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/corporate/prospects/${id}`, { status });
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't update status"));
    }
  };

  const remove = async (id, company) => {
    if (!window.confirm(`Delete prospect "${company}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/corporate/prospects/${id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const copyLink = (slug, company) => {
    const url = `${PUBLIC_ORIGIN}/pitch-deck?via=${slug}`;
    navigator.clipboard?.writeText(url);
    toast.success(`Link copied: ${company}`);
  };

  const summaryCounts = useMemo(() => {
    const counts = data.summary?.by_status || {};
    return STATUSES.map((s) => ({ s, n: counts[s] || 0 }));
  }, [data.summary]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-10" data-testid="corporate-page">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-red-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Cohort dashboard
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="overline mb-2"><span className="red-bar" />Instructor tools</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-2">Corporate pipeline</h1>
            <p className="text-sm text-zinc-700 max-w-2xl leading-relaxed">
              Track companies you've sent the pitch deck to. Each prospect gets a unique tracking
              link &mdash; when they open it, click "Book a call", or you mark them signed, their
              row updates here in real time.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="btn-outline text-xs" data-testid="corp-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={() => setShowAddForm((v) => !v)} className="btn-primary text-xs" data-testid="corp-add-toggle">
              <Plus className="w-3.5 h-3.5" /> {showAddForm ? "Cancel" : "Add prospect"}
            </button>
          </div>
        </div>

        {/* Pipeline summary */}
        <div className="grid grid-cols-2 sm:grid-cols-7 gap-2 mb-6" data-testid="corp-summary">
          {summaryCounts.map(({ s, n }) => (
            <div key={s} className={`px-3 py-2 border ${STATUS_COLOR[s]}`} data-testid={`corp-summary-${s}`}>
              <div className="font-heading text-2xl font-bold tracking-tight leading-none">{n}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest mt-0.5">{s}</div>
            </div>
          ))}
        </div>

        {/* Add prospect form */}
        {showAddForm && (
          <form onSubmit={onAdd} className="card-tech p-5 mb-6" data-testid="corp-add-form">
            <h3 className="font-heading text-lg font-bold mb-3">New prospect</h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <label className="text-xs">
                <div className="font-mono uppercase tracking-widest text-zinc-500 mb-1">Company *</div>
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="corp-form-company"
                  placeholder="ACME Fire & Safety" required />
              </label>
              <label className="text-xs">
                <div className="font-mono uppercase tracking-widest text-zinc-500 mb-1">Contact name</div>
                <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="corp-form-contact-name"
                  placeholder="Jane Operations Manager" />
              </label>
              <label className="text-xs">
                <div className="font-mono uppercase tracking-widest text-zinc-500 mb-1">Contact email</div>
                <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="corp-form-contact-email"
                  placeholder="jane@acmefire.com" />
              </label>
              <label className="text-xs">
                <div className="font-mono uppercase tracking-widest text-zinc-500 mb-1">Estimated seats</div>
                <input type="number" min="1" max="500" value={form.seats_estimate}
                  onChange={(e) => setForm({ ...form, seats_estimate: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="corp-form-seats"
                  placeholder="15" />
              </label>
            </div>
            <label className="text-xs block mb-3">
              <div className="font-mono uppercase tracking-widest text-zinc-500 mb-1">Notes</div>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows="2" className="w-full border border-zinc-300 px-3 py-2 text-sm" data-testid="corp-form-notes"
                placeholder="Mentioned they have 3 apprentices going for NICET 1 this fall…" />
            </label>
            <button type="submit" disabled={saving} className="btn-primary text-xs" data-testid="corp-form-submit">
              {saving ? "Saving…" : "Save + generate tracking link"}
            </button>
          </form>
        )}

        {/* Prospects table */}
        {loading ? (
          <div className="text-zinc-500 py-10 text-center">Loading…</div>
        ) : data.prospects.length === 0 ? (
          <div className="card-tech p-10 text-center" data-testid="corp-empty">
            <Building2 className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <h3 className="font-heading text-xl font-bold mb-2">No prospects yet</h3>
            <p className="text-sm text-zinc-600 max-w-md mx-auto mb-4">
              Add your first target company above. You'll get a unique tracking URL to email them.
            </p>
            <button onClick={() => setShowAddForm(true)} className="btn-primary text-xs" data-testid="corp-empty-add">
              <Plus className="w-3.5 h-3.5" /> Add first prospect
            </button>
          </div>
        ) : (
          <div className="card-tech overflow-x-auto" data-testid="corp-table">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  <th className="text-left px-4 py-3">Company</th>
                  <th className="text-left px-4 py-3">Contact</th>
                  <th className="text-left px-4 py-3">Seats</th>
                  <th className="text-left px-4 py-3">Engagement</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.prospects.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`corp-row-${p.slug}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{p.company}</div>
                      <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{p.slug}</div>
                    </td>
                    <td className="px-4 py-3 text-zinc-700">
                      {p.contact_name && <div>{p.contact_name}</div>}
                      {p.contact_email && (
                        <a href={`mailto:${p.contact_email}`} className="text-[11px] text-zinc-500 hover:text-red-600">
                          {p.contact_email}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-700">{p.seats_estimate || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3 text-xs">
                        <span className="inline-flex items-center gap-1 text-zinc-700" title="Views"><Eye className="w-3 h-3" />{p.view_count || 0}</span>
                        <span className="inline-flex items-center gap-1 text-zinc-700" title="CTA clicks"><MousePointerClick className="w-3 h-3" />{p.cta_clicks || 0}</span>
                        {p.last_view_at && <span className="text-[10px] text-zinc-500">last {fmtRelative(p.last_view_at)} ago</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={p.status}
                        onChange={(e) => updateStatus(p.id, e.target.value)}
                        className={`text-[11px] font-mono uppercase tracking-wider px-2 py-1 border ${STATUS_COLOR[p.status]}`}
                        data-testid={`corp-status-${p.slug}`}
                      >
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => copyLink(p.slug, p.company)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-zinc-100" title="Copy tracking link" data-testid={`corp-copy-${p.slug}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <a href={`/pitch-deck?via=${p.slug}`} target="_blank" rel="noreferrer" className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-zinc-100" title="Preview pitch deck" data-testid={`corp-preview-${p.slug}`}>
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                        {p.contact_email && (
                          <a href={`mailto:${p.contact_email}?subject=APM%20Fire%20Alarm%20Corporate%20Training&body=Hey%20${encodeURIComponent(p.contact_name || "")}%2C%0A%0AHere%27s%20the%20short%20pitch%20deck%20I%20mentioned%3A%20${encodeURIComponent(PUBLIC_ORIGIN + "/pitch-deck?via=" + p.slug)}%0A%0AHappy%20to%20jump%20on%20a%2015-min%20call%20if%20any%20questions.%0A%0A%E2%80%94%20Trevor`} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-zinc-100" title="Email contact" data-testid={`corp-mail-${p.slug}`}>
                            <Mail className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button onClick={() => remove(p.id, p.company)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50" title="Delete" data-testid={`corp-delete-${p.slug}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-6 p-4 bg-zinc-50 border-l-4 border-zinc-300 text-xs text-zinc-700 leading-relaxed">
          <strong>Pro tip:</strong> Each prospect's tracking URL is <code className="bg-white px-1.5 py-0.5">{PUBLIC_ORIGIN}/pitch-deck?via=&#123;slug&#125;</code>.
          Email that exact link &mdash; when they open it, "emailed" auto-advances to "opened",
          then to "clicked" when they hit the Book-a-Call CTA. You stay informed without
          installing tracking pixels.
        </div>
      </main>
    </div>
  );
}
